import { nanoid } from 'nanoid';

const MAX_INSTRUMENT_DISPLAY_ITEMS = 6;
const MAX_DISPLAY_TOKEN_LENGTH = 64;

const sanitizeText = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  return trimmed;
};

const ensureId = (candidate, fallbackIndex) => {
  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim();
  }
  return `instrument-${fallbackIndex}-${nanoid(8)}`;
};

const normalizeListInput = (raw) => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Failed to parse instrument experience payload.', error);
      return [];
    }
  }
  if (raw && typeof raw === 'object') {
    return [raw];
  }
  return [];
};

const toNormalizedExperience = (experience, index, { preserveEmptyInstrument = true } = {}) => {
  const normalized = {
    id: ensureId(experience?.id, index),
    instrument: sanitizeText(experience?.instrument || ''),
    years: sanitizeText(experience?.years || ''),
  };

  if (!preserveEmptyInstrument && !normalized.instrument) {
    return null;
  }

  return normalized;
};

export const normalizeInstrumentExperiences = (raw) => {
  const source = normalizeListInput(raw);
  return source
    .map((experience, index) => toNormalizedExperience(experience, index))
    .filter(Boolean);
};

export const getFilledInstrumentExperiences = (raw) => {
  const source = normalizeListInput(raw);
  return source
    .map((experience, index) => toNormalizedExperience(experience, index, { preserveEmptyInstrument: false }))
    .filter(Boolean);
};

export const serializeInstrumentExperiences = (experiences, { defaultYears } = {}) => {
  const list = Array.isArray(experiences) ? experiences : [];
  const sanitized = list
    .map((experience, index) => {
      const normalized = toNormalizedExperience(experience, index);
      if (!normalized.instrument) {
        return null;
      }
      if (!normalized.years && defaultYears) {
        return {
          ...normalized,
          years: defaultYears,
        };
      }
      return normalized;
    })
    .filter(Boolean);

  return JSON.stringify(sanitized);
};

const sanitizeDisplayToken = (value, maxLength = MAX_DISPLAY_TOKEN_LENGTH) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value
    .replace(/[\r\n\t]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);
};

export const formatInstrumentExperienceDisplay = (
  raw,
  {
    itemSeparator = ', ',
    yearsSeparator = ' - ',
    maxItems = MAX_INSTRUMENT_DISPLAY_ITEMS,
  } = {},
) => {
  const resolvedItemSeparator = typeof itemSeparator === 'string' ? itemSeparator : ', ';
  const resolvedYearsSeparator = typeof yearsSeparator === 'string' ? yearsSeparator : ' - ';
  const resolvedMaxItems = Number.isInteger(maxItems) && maxItems > 0
    ? maxItems
    : MAX_INSTRUMENT_DISPLAY_ITEMS;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return '';
    }
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
      return sanitizeDisplayToken(trimmed, 256);
    }
  }

  const formatted = getFilledInstrumentExperiences(raw)
    .slice(0, resolvedMaxItems)
    .map((experience) => {
      const instrument = sanitizeDisplayToken(experience.instrument);
      const years = sanitizeDisplayToken(experience.years);
      if (!instrument) {
        return null;
      }
      return years
        ? `${instrument}${resolvedYearsSeparator}${years}`
        : instrument;
    })
    .filter(Boolean);

  if (formatted.length > 0) {
    return formatted.join(resolvedItemSeparator);
  }

  return '';
};

export const promoteInstrumentExperience = (experiences, target) => {
  const list = normalizeInstrumentExperiences(experiences);
  if (list.length === 0 || !target) {
    return list;
  }

  const targetId = typeof target === 'string'
    ? target
    : (typeof target === 'object' && target !== null && (target.id || target.instrument)) || null;

  if (!targetId) {
    return list;
  }

  const index = list.findIndex(
    (experience) => experience.id === targetId || experience.instrument === targetId
  );

  if (index <= 0) {
    return list;
  }

  const updated = [...list];
  const [selected] = updated.splice(index, 1);
  return [selected, ...updated];
};
